/**
 * autoresponders.service.ts
 *
 * Typed wrappers for Admin → Auto Responders: the rules, the editor's choices
 * and the recent automatic replies. All six endpoints live under
 * /communications/autoresponders and answer with the usual {data, meta}
 * envelope; this module returns the unwrapped data.
 *
 * Refusals come back as 422 (never 403, which the client turns into a
 * redirect): VALIDATION_ERROR carries error.details.fields keyed by the same
 * camelCase names as the payload, ADMIN_REQUIRED means the caller is not an
 * administrator. A rule outside the caller's practice is a 404 NOT_FOUND.
 */
import { apiClient } from './apiClient';
import type { ApiResponse } from '@/types/api.types';

// ─── Types ────────────────────────────────────────────────────────────────

export type AutoresponderEventType = 'inbound_sms' | 'new_lead' | 'email_reply';
export type AutoresponderEmailReplyKind = 'practice' | 'marketing';
export type AutoresponderChannel = 'sms' | 'email';
export type AutoresponderActivityStatus = 'claimed' | 'sent' | 'skipped' | 'failed' | 'unknown';

/** Why a rule cannot work as set up. `message` is already plain words. */
export interface AutoresponderRuleWarning {
  code: string;
  message: string;
}

export interface AutoresponderRule {
  id: number;
  name: string;
  eventType: AutoresponderEventType;
  /** Ten digits, or null for "any of the practice's numbers". */
  practiceNumber: string | null;
  practiceNumberLabel: string | null;
  /** The first of leadSourceIds, kept by the server for pages from before the lists. */
  leadSourceId: number | null;
  leadSourceName: string | null;
  /**
   * A new-lead rule's lead sources; [] for any. The four list fields are
   * absent from a server older than the lists: one merge deploys this page
   * before the server, so read them with a fallback.
   */
  leadSourceIds?: number[];
  /** Names in the same order; null for one that no longer exists. */
  leadSources?: AutoresponderNamedChoice[];
  /** A new-lead rule's plan types; [] for any. */
  planTypeIds?: number[];
  planTypes?: AutoresponderNamedChoice[];
  emailReplyKind: AutoresponderEmailReplyKind | null;
  smsTemplateId: number | null;
  smsTemplateName: string | null;
  emailTemplateId: number | null;
  emailTemplateName: string | null;
  emailHeaderFooterId: number | null;
  emailHeaderFooterName: string | null;
  outsideOfficeHoursOnly: boolean;
  /** The first of officeHoursFacilityIds, kept by the server for pages from before the list. */
  officeHoursFacilityId: number | null;
  officeHoursFacilityName: string | null;
  /**
   * The facilities whose office hours an after-hours rule follows; it replies
   * only while every one is closed. [] when the rule answers at any time.
   * Absent from a server older than the list, like the lead-source lists:
   * read them with a fallback (savedFacilities).
   */
  officeHoursFacilityIds?: number[];
  /** Names in the same order; null for one that no longer exists. */
  officeHoursFacilities?: AutoresponderNamedChoice[];
  repeatWindowHours: number;
  active: boolean;
  /** Pacific wall clock, "YYYY-MM-DD HH:MM:SS". */
  createdAt: string;
  updatedAt: string | null;
  warnings: AutoresponderRuleWarning[];
}

/** What a create sends, and what an update may send any subset of. */
export interface AutoresponderRulePayload {
  name: string;
  eventType: AutoresponderEventType;
  practiceNumber: string | null;
  /** A lead must be in every list a rule sets; [] for any. Lead rules only. */
  leadSourceIds: number[];
  planTypeIds: number[];
  emailReplyKind: AutoresponderEmailReplyKind | null;
  smsTemplateId: number | null;
  emailTemplateId: number | null;
  emailHeaderFooterId: number | null;
  outsideOfficeHoursOnly: boolean;
  /** Every facility must be closed for the rule to reply; [] when after-hours is off. */
  officeHoursFacilityIds: number[];
  repeatWindowHours: number;
  active: boolean;
}

export interface AutoresponderNamedChoice {
  id: number;
  name: string | null;
}

export interface AutoresponderOptionGroup {
  categoryId: number;
  categoryName: string;
  items: { id: number; name: string }[];
}

export interface AutoresponderOptions {
  eventTypes: { value: AutoresponderEventType; label: string }[];
  emailReplyKinds: { value: AutoresponderEmailReplyKind; label: string }[];
  smsTemplates: AutoresponderOptionGroup[];
  emailTemplates: AutoresponderOptionGroup[];
  headerFooters: { id: number; name: string }[];
  practiceNumbers: { value: string; label: string }[];
  facilities: { id: number; name: string; hasUsableHours: boolean }[];
  leadSources: AutoresponderOptionGroup[];
  /** Absent from a server older than the lists (see AutoresponderRule). */
  planTypes?: { id: number; name: string }[];
  repeatWindowHours: { min: number; max: number; default: number };
}

/** One automatic reply, or one that was held back. Newest first. */
export interface AutoresponderActivityRow {
  id: number;
  ruleId: number | null;
  ruleName: string | null;
  ruleDeleted: boolean;
  eventType: string;
  channel: AutoresponderChannel;
  /** Already masked by the server: "(•••) •••-1234" or "j•••@example.com". */
  recipient: string;
  status: AutoresponderActivityStatus;
  /** A stored reason code, or '' for a reply that went out. */
  reason: string;
  /** Pacific wall clock, "YYYY-MM-DD HH:MM:SS". */
  createdAt: string;
}

// ─── Calls ────────────────────────────────────────────────────────────────

const BASE = '/communications/autoresponders';

export function listAutoresponderRules(): Promise<AutoresponderRule[]> {
  return apiClient.get<ApiResponse<AutoresponderRule[]>>(BASE).then((r) => r.data.data);
}

export function getAutoresponderOptions(): Promise<AutoresponderOptions> {
  return apiClient
    .get<ApiResponse<AutoresponderOptions>>(`${BASE}/options`)
    .then((r) => r.data.data);
}

export function listAutoresponderActivity(ruleId?: number | null): Promise<AutoresponderActivityRow[]> {
  return apiClient
    .get<ApiResponse<AutoresponderActivityRow[]>>(`${BASE}/activity`, {
      params: ruleId ? { ruleId } : undefined,
    })
    .then((r) => r.data.data);
}

export function createAutoresponderRule(data: AutoresponderRulePayload): Promise<AutoresponderRule> {
  return apiClient
    .post<ApiResponse<AutoresponderRule>>(BASE, data)
    .then((r) => r.data.data);
}

/** Partial on purpose: `{ active: false }` alone turns a rule off. */
export function updateAutoresponderRule(
  id: number,
  data: Partial<AutoresponderRulePayload>,
): Promise<AutoresponderRule> {
  return apiClient
    .put<ApiResponse<AutoresponderRule>>(`${BASE}/${id}`, data)
    .then((r) => r.data.data);
}

export function deleteAutoresponderRule(id: number): Promise<void> {
  return apiClient.delete(`${BASE}/${id}`).then(() => undefined);
}
